<script lang="ts">
    import { createEventDispatcher, onMount } from 'svelte';
    import Swiper from 'swiper';
    import { Navigation, Pagination } from 'swiper/modules';
    import 'swiper/css/bundle';
    import LogoClawbuster from '@src/cross-reusable-components/logo/logo-clawbuster.svelte';
    import LogoGame from '@src/components/ui/logo/logo-game.svelte';
    import { trackEvent } from '@root/src/utils/init-analytics';

    export let slidesInfo = [];

    const slideTitleClasses = '!font-bold !text-center !text-2xl tablet:!text-3.5xl';
    const slideDescClasses = '!text-md text-cross-n-100/80';
    let carouselEl;
    const dispatch = createEventDispatcher();

    onMount(() => {
        initCarousel();
    });

    const initCarousel = () => {
        let countSlideChanges = 0;
        new Swiper(carouselEl, {
            spaceBetween: 40,
            slidesPerView: 1,
            grabCursor: true,
            centeredSlides: true,
            modules: [Navigation, Pagination],
            navigation: {
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev'
            },
            pagination: {
                el: '.swiper-pagination',
                clickable: true,
                renderBullet: function (index, className) {
                    return `<span class="${className}">
                      <span></span>
                    </span>
            `;
                }
            },
            on: {
                init: swiper => {
                    trackEvent('welcome_page_state_change', {
                        welcome_page_number: swiper.activeIndex
                    });
                },
                slideChange: swiper => {
                    countSlideChanges++;

                    dispatch('slide-change', countSlideChanges);
                    dispatch('current-slide-index', swiper.activeIndex);

                    trackEvent('welcome_page_state_change', {
                        welcome_page_number: swiper.activeIndex
                    });
                }
            }
        });
    };
</script>

<div
    class="wrapper relative mx-auto my-0 flex h-full w-full flex-col flex-nowrap items-center justify-center gap-4 px-2 pb-6 pt-4 tablet:!max-w-xl"
>
    <div class="absolute inset-x-0 top-5 flex justify-center">
        <LogoClawbuster class="w-18 phone:w-24 tablet:w-24 desktop:w-24" />
    </div>

    <section
        class="relative mx-auto mb-0 mt-10 flex h-full w-full flex-col flex-nowrap items-center justify-center gap-4 tablet:h-auto tablet:!max-w-xl"
    >
        <div class="swiper w-full max-w-md desktop:mt-32" bind:this={carouselEl}>
            <div
                class="pointer-events-none fixed left-1/2 z-10 -translate-x-1/2 -translate-y-14 s-phone:-translate-y-16 phone:-translate-y-32 tablet:-translate-y-[7.5rem]"
            >
                <div class="hidden s-phone:block s-phone:w-40 phone:w-64 tablet:w-64 desktop:w-72">
                    <LogoGame />
                </div>
            </div>
            <div class="swiper-wrapper relative">
                {#each slidesInfo as slideInfo, key}
                    <div class="swiper-slide flex h-full flex-col gap-4 desktop:gap-6">
                        <div class="relative">
                            <img src={slideInfo.img_slide} class="object-cover" alt="slide {key}" loading="lazy" />
                        </div>

                        <div class="flex flex-col gap-2 desktop:gap-4">
                            <h4 class={slideTitleClasses}>
                                {slideInfo.title_slide}
                            </h4>
                            <p class={slideDescClasses}>
                                {slideInfo.desc_slide}
                            </p>
                        </div>
                    </div>
                {/each}
            </div>
        </div>

        <div
            class="swiper-button-prev !hidden -translate-x-20 -translate-y-9 !bg-cross-bgrd-secnd after:!content-[''] hover:!bg-cross-bgrd-blue desktop:!block"
        >
            <div class="cstm-position-center mr-0.5">
                <svg width="100%" height="100%" viewBox="0 0 24 24" fill="white">
                    <path
                        d="M16.62 2.99c-.49-.49-1.28-.49-1.77 0L6.54 11.3c-.39.39-.39 1.02 0 1.41l8.31 8.31c.49.49 1.28.49 1.77 0s.49-1.28 0-1.77L9.38 12l7.25-7.25c.48-.48.48-1.28-.01-1.76z"
                    />
                </svg>
            </div>
        </div>

        <div
            class="swiper-button-next !hidden -translate-y-9 translate-x-20 !bg-cross-bgrd-secnd after:!content-[''] hover:!bg-cross-bgrd-blue desktop:!block"
        >
            <div class="cstm-position-center mr-0.5">
                <svg width="100%" height="100%" viewBox="0 0 24 24" fill="white">
                    <path
                        d="M7.38 21.01c.49.49 1.28.49 1.77 0l8.31-8.31c.39-.39.39-1.02 0-1.41L9.15 2.98c-.49-.49-1.28-.49-1.77 0s-.49 1.28 0 1.77L14.62 12l-7.25 7.25c-.48.48-.48 1.28.01 1.76z"
                    />
                </svg>
            </div>
        </div>

        <div class="swiper-pagination mb-4 mt-2 desktop:mb-6" />
    </section>
</div>

<style>
    .swiper-slide {
        color: white;
        text-align: center;
        display: flex;
        justify-content: center;
        align-items: center;
    }

    .swiper-button-prev,
    .swiper-button-next {
        height: 48px;
        width: 48px;
        font-size: 18px;
        border-radius: 50%;
        color: white;
        box-shadow: 0px 6px 6px 0px #0000003d;
    }

    .swiper-button-next.swiper-button-disabled,
    .swiper-button-prev.swiper-button-disabled {
        opacity: 0;
    }

    :global(.swiper-button-prev .swiper-button-disabled) {
        opacity: 0;
    }

    :global(.swiper-pagination) {
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;
        flex-wrap: nowrap;
        flex-shrink: 0;
        flex-grow: 0;
        position: static !important;
        width: 100%;
        gap: 15px;
    }

    :global(.swiper-pagination span) {
        width: 8px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        opacity: 1;
        /* @apply bg-cross-n-300; */
    }

    :global(.swiper-pagination-bullet) {
        @apply border border-cross-n-200;
    }

    :global(.swiper-pagination-bullet-active) {
        width: 8px;
        border-radius: 50%;
        border-width: 0;
        background: linear-gradient(135deg, #5c9cff 0%, #1f77ff 51.71%, #1f77ff 100%) !important;
    }
</style>
